import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, FlagIcon, LockIcon, ScaleIcon, SearchIcon, ShieldIcon, SnowflakeIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input } from '../../components/ui/Field';
import { DataTable } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { useCampaGift } from '../../contexts/CampaGiftContext';
import { DEMO_DATA_NOTICE, limits } from '../../data/campagift';
import type { GiftLedgerAccount } from '../../types/campagift';
import { STATUS_LABEL, bpsLabel, koboToNaira, statusTone } from '../../utils/campagift';
const TABS = ['Overview', 'Instruments', 'Ledger', 'Reconciliation', 'Merchants', 'Risk', 'Configuration', 'Audit'] as const;
const ACCOUNT_LABEL: Record<GiftLedgerAccount, string> = {
  'funding:external': 'External funding (payment provider)',
  'campagift:outstanding': 'CampaGift outstanding value (liability)',
  'merchant:payable': 'Merchant payable / settlement',
  'platform:commission': 'Campuzon commission revenue',
  'platform:fees': 'Campuzon fee revenue',
  'campaign:budget': 'Campaign budget',
  'promotion:merchant': 'Merchant promotional funding',
  'settlement:bank': 'Settlement bank account',
  'breakage:unredeemed': 'Unredeemed value at expiry'
};
export function CampaGiftControl() {
  const {
    gifts,
    redemptions,
    ledger,
    settlements,
    disputes,
    audit,
    merchants,
    types,
    campaigns,
    flags,
    typeOf,
    remaining,
    status,
    reverse,
    freeze,
    unfreeze
  } = useCampaGift();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const [query, setQuery] = useState('');
  const [reverseTarget, setReverseTarget] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [freezeTarget, setFreezeTarget] = useState<string | null>(null);
  const [freezeReason, setFreezeReason] = useState('');

  /* Every figure below is derived from the ledger, never from a stored total. */
  const totals = useMemo(() => {
    const byAccount = new Map<GiftLedgerAccount, number>();
    for (const entry of ledger) {
      for (const line of entry.entries) {
        const signed = line.direction === 'credit' ? line.amountKobo : -line.amountKobo;
        byAccount.set(line.account, (byAccount.get(line.account) ?? 0) + signed);
      }
    }
    return byAccount;
  }, [ledger]);
  const issuedKobo = gifts.reduce((total, gift) => total + gift.faceValueKobo, 0);
  /* Every record counts, including reversed originals — their compensating
     negative entry cancels them, so nothing is refunded twice. */
  const redeemedKobo = redemptions.reduce((total, r) => total + r.amountKobo, 0);
  const outstandingKobo = totals.get('campagift:outstanding') ?? 0;
  const commissionKobo = totals.get('platform:commission') ?? 0;
  const feesKobo = totals.get('platform:fees') ?? 0;
  const payableKobo = totals.get('merchant:payable') ?? 0;
  const expiredValueKobo = gifts.filter(gift => status(gift) === 'expired').reduce((total, gift) => total + remaining(gift), 0);
  const promotionalKobo = gifts.filter(gift => typeOf(gift).funding !== 'user_purchase').reduce((total, gift) => total + gift.faceValueKobo, 0);

  /* Reconciliation identity: issued − redeemed should equal outstanding. */
  const drift = issuedKobo - redeemedKobo - outstandingKobo;
  const filtered = gifts.filter(gift => {
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return gift.publicReference.toLowerCase().includes(needle) || gift.instrumentId.toLowerCase().includes(needle) || gift.holderHandle.toLowerCase().includes(needle) || typeOf(gift).name.toLowerCase().includes(needle) || STATUS_LABEL[status(gift)].toLowerCase().includes(needle);
  });
  const metrics = [{
    label: 'Total issued',
    value: issuedKobo,
    note: `${gifts.length} instruments`
  }, {
    label: 'Total redeemed',
    value: redeemedKobo,
    note: `${redemptions.filter(r => !r.reversalOfReference).length} redemptions, net of reversals`
  }, {
    label: 'Outstanding liability',
    value: outstandingKobo,
    note: 'Unredeemed value held as a liability'
  }, {
    label: 'Merchant payable',
    value: payableKobo,
    note: 'Owed but not yet settled'
  }, {
    label: 'Commission revenue',
    value: commissionKobo,
    note: 'From merchant redemptions'
  }, {
    label: 'Fee revenue',
    value: feesKobo,
    note: 'Purchase and issuance fees'
  }, {
    label: 'Promotional value',
    value: promotionalKobo,
    note: 'Not purchased by a user'
  }, {
    label: 'Expired value',
    value: expiredValueKobo,
    note: 'Unredeemed at expiry, preserved'
  }];
  return <div className="space-y-6" data-id="element-6302" data-mp="2u4pe8jsyhaxb">
      <PageHeader eyebrow="Financial operations" title="CampaGift control centre" description="Instruments, redemptions, merchant settlement, reconciliation and risk. Gift operations, finance, risk and sensitive-data access are separate permissions." actions={<Badge tone="warn" icon={<LockIcon aria-hidden className="h-4 w-4" data-id="element-6305" data-mp="3d2igdjvr9gtn" />} data-id="element-6304" data-mp="3d28sc1u9tjql">
            Compliance gate open
          </Badge>} data-id="element-6303" data-mp="2pcxw8xnv8dis" />

      <p className="rounded-card border border-line bg-canvas px-4 py-2.5 text-caption text-ink-muted" data-id="element-6306" data-mp="384gvcterr7fj">
        {DEMO_DATA_NOTICE}
      </p>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="CampaGift administration sections" data-id="element-6307" data-mp="2y4zuqqedjjqf" />

      {tab === 'Overview' && <div className="space-y-6" data-id="element-6308" data-mp="1rp5wbvxfay7i">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-6309" data-mp="1rq78y4u37src">
            {metrics.map((metric, index) => <div key={metric.label} className={`rounded-card border bg-surface px-5 py-4 ${index === 2 ? 'border-brand-700' : 'border-line'}`} data-id="element-6310" data-mp="1ms5r1n8edzhg" data-mpi={index}>
                <p className="text-body-sm text-ink-muted" data-id="element-6311" data-mp="4splhmkkxx2o">{metric.label}</p>
                <p className={`cz-num mt-1.5 font-semibold text-ink ${index === 2 ? 'text-h1' : 'text-h2'}`} data-id="element-6312" data-mp="9rannrje6qtr">
                  {koboToNaira(metric.value)}
                </p>
                <p className="mt-1 text-caption text-ink-muted" data-id="element-6313" data-mp="3vzalhvsm4fbu">{metric.note}</p>
              </div>)}
          </div>

          <div className="grid gap-6 lg:grid-cols-2" data-id="element-6314" data-mp="3i4zrp01rduyx">
            <Panel title="Campaigns" bodyClassName="p-0" data-id="element-6315" data-mp="3n5dxscjsuuub">
              <ul className="divide-y divide-line" data-id="element-6316" data-mp="3n3t933k6264d">
                {campaigns.map(campaign => <li key={campaign.id} className="px-5 py-4" data-id="element-6317" data-mp="3d7zvrhymi8lj">
                    <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-6318" data-mp="3d7hta3lday56">
                      <p className="text-body font-semibold text-ink" data-id="element-6319" data-mp="2tmzduh00gclb">{campaign.name}</p>
                      <Badge tone={campaign.state === 'running' ? 'ok' : 'neutral'} plain data-id="element-6320" data-mp="2plnvmwjgutn5">
                        {campaign.state}
                      </Badge>
                    </div>
                    <p className="cz-num mt-1 text-caption text-ink-muted" data-id="element-6321" data-mp="2yl15hqbi5k8e">
                      {koboToNaira(campaign.issuedKobo)} of {koboToNaira(campaign.budgetKobo)} issued ·{' '}
                      {campaign.issuedCount}/{campaign.maxGifts} gifts · {campaign.startsAt} →{' '}
                      {campaign.endsAt}
                    </p>
                    <p className="mt-1 text-caption text-ink-muted" data-id="element-6322" data-mp="3mcziqfuihpo6">Owner: {campaign.owner}</p>
                  </li>)}
              </ul>
              <p className="border-t border-line px-5 py-3 text-caption text-ink-muted" data-id="element-6323" data-mp="2vjc0av7e8z">
                Issuance stops when either the budget or the gift count is reached, whichever comes
                first.
              </p>
            </Panel>

            <Panel title="Disputes" bodyClassName="p-0" data-id="element-6324" data-mp="2c002bn95mq2t">
              <ul className="divide-y divide-line" data-id="element-6325" data-mp="2gvp1vql6c6z4">
                {disputes.map(dispute => <li key={dispute.id} className="px-5 py-4" data-id="element-6326" data-mp="2lx4r7uudwznb">
                    <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-6327" data-mp="2qumoup2ybsg0">
                      <p className="text-body font-semibold text-ink" data-id="element-6328" data-mp="1t75ynpgzekdw">{dispute.kind}</p>
                      <Badge tone={dispute.state === 'resolved' ? 'ok' : dispute.state === 'rejected' ? 'danger' : 'warn'} data-id="element-6329" data-mp="1t5b9fyyog640">
                        {dispute.state.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-6330" data-mp="1feprqdkym27p">
                      {dispute.reference} · {dispute.raisedBy} ({dispute.raisedByRole}) ·{' '}
                      {dispute.at}
                    </p>
                    <p className="mt-1.5 text-body-sm text-ink-muted" data-id="element-6331" data-mp="3op7b1xl07nei">{dispute.detail}</p>
                    {dispute.decision && <p className="mt-2 rounded-md border border-line bg-canvas px-3 py-2 text-body-sm text-ink" data-id="element-6332" data-mp="3eqhz0lfsgx66">
                        <span className="font-semibold" data-id="element-6333" data-mp="39retkzvwcoxo">{dispute.reviewer}:</span>{' '}
                        {dispute.decision}
                      </p>}
                  </li>)}
              </ul>
            </Panel>
          </div>
        </div>}

      {tab === 'Instruments' && <div className="space-y-4" data-id="element-6334" data-mp="2w11px9q1fwe5">
          <Panel title="Search instruments" data-id="element-6335" data-mp="2vxxbljiuzk9r">
            <Field label="Reference, instrument id, holder, type or status" htmlFor="gift-search" hint="Administrative search is scoped by permission and written to the audit log." data-id="element-6336" data-mp="2qzlzt3906zow">
              <Input id="gift-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="CG-4H8K2M" data-id="element-6337" data-mp="1bg45dmz0ss2t" />
            </Field>
          </Panel>

          <Panel title={`${filtered.length} instrument${filtered.length === 1 ? '' : 's'}`} bodyClassName="p-0" data-id="element-6338" data-mp="1g8f6uu9eslxh">
            <DataTable caption="CampaGift instruments matching the current search" rowKey={gift => gift.id} rows={filtered} columns={[{
          header: 'Reference',
          primary: true,
          cell: gift => <span className="cz-num" data-id="element-6340" data-mp="wjejvbm3em8r">
                      {gift.publicReference}
                      <span className="mt-0.5 block text-caption font-normal text-ink-muted" data-id="element-6341" data-mp="2cxmqfwqgwkyr">
                        {gift.instrumentId}
                      </span>
                    </span>
        }, {
          header: 'Type',
          cell: gift => typeOf(gift).name
        }, {
          header: 'Holder',
          cell: gift => <span className="cz-num" data-id="element-6342" data-mp="26jr007push10">@{gift.holderHandle}</span>
        }, {
          header: 'Face value',
          align: 'right',
          cell: gift => <span className="cz-num" data-id="element-6343" data-mp="2z7d2tdr9bhmt">{koboToNaira(gift.faceValueKobo)}</span>
        }, {
          header: 'Remaining',
          align: 'right',
          cell: gift => <span className="cz-num font-semibold" data-id="element-6344" data-mp="2pha91bcb9znf">{koboToNaira(remaining(gift))}</span>
        }, {
          header: 'Status',
          status: true,
          cell: gift => <Badge tone={statusTone(status(gift))} plain={status(gift) === 'available'} data-id="element-6345" data-mp="3mxpkhgcb0sql">
                      {STATUS_LABEL[status(gift)]}
                    </Badge>
        }, {
          header: 'Risk action',
          cell: gift => gift.riskState === 'frozen' ? <Button size="sm" variant="secondary" onClick={() => unfreeze(gift.id, 'Investigation closed, no abuse found', 'Risk · tobi.a')} data-id="element-6346" data-mp="3mwurndb4rwge">
                        Unfreeze
                      </Button> : <Button size="sm" variant="ghost" icon={<SnowflakeIcon aria-hidden className="h-4 w-4" data-id="element-6348" data-mp="2q4r4o6zgggja" />} onClick={() => setFreezeTarget(gift.id)} data-id="element-6347" data-mp="2z3kxrndkuos3">
                        Freeze
                      </Button>
        }]} data-id="element-6339" data-mp="ske1tvbfvnmt" />
          </Panel>

          <Panel title="Redemptions and reversals" bodyClassName="p-0" data-id="element-6349" data-mp="1enmv03i6z11c">
            <ul className="divide-y divide-line" data-id="element-6350" data-mp="1elaezvvzv0qx">
              {redemptions.map(item => <li key={item.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5" data-id="element-6351" data-mp="1jkdlzlvjbh1j">
                  <div className="min-w-0 flex-1" data-id="element-6352" data-mp="mu4e3d8g8z0l">
                    <p className="cz-num text-body-sm font-medium text-ink" data-id="element-6353" data-mp="qyu80wjjs3iv">{item.reference}</p>
                    <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-6354" data-mp="vwn9xddtibdc">
                      {koboToNaira(item.amountKobo)} ·{' '}
                      {merchants.find(m => m.id === item.merchantId)?.name} ·{' '}
                      {item.at.slice(0, 10)} · {item.actor}
                    </p>
                    {item.reason && <p className="mt-0.5 text-caption text-ink-muted" data-id="element-6355" data-mp="85843amu1bxc">Reason: {item.reason}</p>}
                  </div>
                  {item.reversalOfReference ? <Badge tone="warn" data-id="element-6356" data-mp="25wq2esk0ab32">Reversal of {item.reversalOfReference}</Badge> : item.state === 'reversed' ? <Badge tone="danger" data-id="element-6357" data-mp="2evtlhwj6aqhk">Reversed</Badge> : <Button size="sm" variant="secondary" onClick={() => {
              setReverseTarget(item.reference);
              setReverseReason('');
              setReverseError(null);
            }} data-id="element-6358" data-mp="2etqm4lw4rl06">
                      Reverse
                    </Button>}
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Ledger' && <Panel title="CampaGift ledger" description="Every material event writes a paired debit and credit. Entries are immutable; corrections are compensating entries." bodyClassName="p-0" data-id="element-6359" data-mp="146b8pkdc10ry">
          <ul className="divide-y divide-line" data-id="element-6360" data-mp="y2oippwuv8jo">
            {ledger.map(entry => <li key={entry.id} className="px-5 py-4" data-id="element-6361" data-mp="kb085r298s4t">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1" data-id="element-6362" data-mp="pamj1tum1wag">
                  <div className="min-w-0" data-id="element-6363" data-mp="adlrbqcf61bz">
                    <p className="text-body font-medium text-ink" data-id="element-6364" data-mp="acu699gg7lzy">{entry.narrative}</p>
                    <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-6365" data-mp="fdrm3fdzk3ya">
                      {entry.reference} · {entry.event.replace('_', ' ')} ·{' '}
                      {entry.at.slice(0, 10)}
                      {entry.providerReference ? ` · provider ref ${entry.providerReference}` : ''}
                    </p>
                  </div>
                  <span className="cz-num text-body font-semibold text-ink" data-id="element-6366" data-mp="1vp40kst7287r">
                    {koboToNaira(entry.entries[0].amountKobo)}
                  </span>
                </div>
                <dl className="mt-2 grid gap-2 sm:grid-cols-2" data-id="element-6367" data-mp="1y9a1ssqfbkkm">
                  {entry.entries.map(line => <div key={line.direction} className="rounded-md border border-line bg-canvas px-3 py-2" data-id="element-6368" data-mp="1uc5k4t3frs0f">
                      <dt className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-6369" data-mp="1kdg1uz7mo5jn">
                        {line.direction}
                      </dt>
                      <dd className="mt-0.5 text-body-sm text-ink" data-id="element-6370" data-mp="2d1c0wako69jl">
                        {ACCOUNT_LABEL[line.account]}
                      </dd>
                    </div>)}
                </dl>
                {entry.correctsReference && <p className="cz-num mt-2 text-caption text-ink-muted" data-id="element-6371" data-mp="30plewqbn5t06">
                    Corrects {entry.correctsReference} — the original entry remains in place.
                  </p>}
              </li>)}
          </ul>
          <p className="flex items-start gap-2 border-t border-line px-5 py-3 text-caption text-ink-muted" data-id="element-6372" data-mp="33bui88nasoy4">
            <ScaleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-6373" data-mp="389mjvudqvpfi" />
            The accounting treatment shown here is architectural. Final treatment of outstanding gift
            value, breakage and commission must be reviewed by qualified accounting, tax and
            regulatory professionals for each jurisdiction.
          </p>
        </Panel>}

      {tab === 'Reconciliation' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-6374" data-mp="3r65me0u1mf33">
          <Panel title="Reconciliation identity" data-id="element-6375" data-mp="3w09syizecl3n">
            <p className="text-body-sm text-ink-muted" data-id="element-6376" data-mp="4snud4rjb8tn">
              Issued value less confirmed redemptions must equal the outstanding liability on the
              ledger. Any difference is a defect, not a rounding artefact.
            </p>
            <dl className="mt-4 divide-y divide-line rounded-card border border-line" data-id="element-6377" data-mp="du58wipan2g8">
              <div className="flex justify-between px-4 py-3" data-id="element-6378" data-mp="z396ezgotyrc">
                <dt className="text-body text-ink-muted" data-id="element-6379" data-mp="v2p9nx010x5q">Issued (instruments)</dt>
                <dd className="cz-num text-body text-ink" data-id="element-6380" data-mp="v4s1mmkqp0d3">{koboToNaira(issuedKobo)}</dd>
              </div>
              <div className="flex justify-between px-4 py-3" data-id="element-6381" data-mp="19043rf185t41">
                <dt className="text-body text-ink-muted" data-id="element-6382" data-mp="143wwnix3ebki">Less redeemed</dt>
                <dd className="cz-num text-body text-ink" data-id="element-6383" data-mp="gd158mwjtids">−{koboToNaira(redeemedKobo).replace('₦', '₦')}</dd>
              </div>
              <div className="flex justify-between px-4 py-3" data-id="element-6384" data-mp="bgaumf3ijezr">
                <dt className="text-body text-ink-muted" data-id="element-6385" data-mp="q3vum053cy3g">Outstanding on ledger</dt>
                <dd className="cz-num text-body text-ink" data-id="element-6386" data-mp="ld5q0p4hoz9t">{koboToNaira(outstandingKobo)}</dd>
              </div>
              <div className={`flex items-center justify-between px-4 py-3 ${drift === 0 ? 'bg-ok-50' : 'bg-danger-50'}`} data-id="element-6387" data-mp="3ts5zs2zv3l8q">
                <dt className="flex items-center gap-2 text-body font-semibold text-ink" data-id="element-6388" data-mp="24b20093s9o2n">
                  {drift === 0 ? <CheckCircle2Icon aria-hidden className="h-5 w-5 text-ok-700" data-id="element-6389" data-mp="293266jag5d7o" /> : <AlertTriangleIcon aria-hidden className="h-5 w-5 text-danger-700" data-id="element-6390" data-mp="1vc6w53o7oq2i" />}
                  {drift === 0 ? 'Reconciled' : 'Discrepancy'}
                </dt>
                <dd className="cz-num text-h4 font-semibold text-ink" data-id="element-6391" data-mp="2n3cxtojkyvtw">{koboToNaira(drift)}</dd>
              </div>
            </dl>

            <h3 className="mt-6 text-h4 font-semibold text-ink" data-id="element-6392" data-mp="2rz203zmziqtw">Settlement reconciliation</h3>
            <ul className="mt-2 divide-y divide-line rounded-card border border-line" data-id="element-6393" data-mp="2e13lct4r4sdv">
              {settlements.map(settlement => {
            const behind = redemptions.filter(r => settlement.redemptionReferences.includes(r.reference));
            const recomputed = behind.reduce((total, r) => total + r.netReceivableKobo, 0);
            const matches = recomputed === settlement.netKobo;
            return <li key={settlement.id} className="px-4 py-3" data-id="element-6394" data-mp="1msgtk5m2avf8">
                    <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-6395" data-mp="17x0tnjysw5ax">
                      <p className="cz-num text-body-sm font-medium text-ink" data-id="element-6396" data-mp="1cvc3hup3ift5">
                        {settlement.reference}
                      </p>
                      <Badge tone={matches ? 'ok' : 'danger'} data-id="element-6397" data-mp="z2mhvrdhbcwk">
                        {matches ? 'Matches redemptions' : 'Unmatched'}
                      </Badge>
                    </div>
                    <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-6398" data-mp="p7cbysjymldi">
                      Settlement {koboToNaira(settlement.netKobo)} · recomputed from{' '}
                      {behind.length} redemptions {koboToNaira(recomputed)}
                      {settlement.providerReference ? ` · provider ${settlement.providerReference}` : ' · no provider reference yet'}
                    </p>
                  </li>;
          })}
            </ul>
          </Panel>

          <div className="space-y-6" data-id="element-6399" data-mp="3sra7yuv1obn1">
            <Panel title="What reconciliation looks for" data-id="element-6400" data-mp="ajkp2srlgnvy">
              <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-6401" data-mp="agy8y4qn1024">
                <li data-id="element-6402" data-mp="5ioddt103xfz">· Missing or duplicate transactions</li>
                <li data-id="element-6403" data-mp="kgys9qoibg6b">· Unmatched provider references</li>
                <li data-id="element-6404" data-mp="kfe027po09bm">· Redemptions with no settlement after their eligibility window</li>
                <li data-id="element-6405" data-mp="fi5gc2ovbx89">· Incorrect commission against the rule in force at the time</li>
                <li data-id="element-6406" data-mp="t8iq8alblm08">· Failed reversals leaving value in two places</li>
                <li data-id="element-6407" data-mp="p7yqr0d9y6ax">· Instruments whose remaining value disagrees with their ledger position</li>
              </ul>
            </Panel>
            <Panel title="Never edit history" data-id="element-6408" data-mp="138huhvapiq7a">
              <p className="text-body-sm text-ink-muted" data-id="element-6409" data-mp="y95cbnus67b1">
                Correcting a figure means writing a compensating entry with a reason and an authorised
                actor. No administrative screen can alter a posted transaction.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Merchants' && <Panel title="Participating merchants" bodyClassName="p-0" data-id="element-6410" data-mp="4um5iblyp91d">
          <DataTable caption="Merchants participating in CampaGift" rowKey={merchant => merchant.id} rows={merchants} columns={[{
        header: 'Merchant',
        primary: true,
        cell: merchant => <span data-id="element-6412" data-mp="nnq1i3mnjxz5">
                    {merchant.name}
                    <span className="mt-0.5 block text-caption font-normal text-ink-muted" data-id="element-6413" data-mp="slrhugsp1j6x">
                      {merchant.category}
                    </span>
                  </span>
      }, {
        header: 'Campus',
        cell: merchant => merchant.campus
      }, {
        header: 'CampaGift',
        cell: merchant => <Badge tone={merchant.campaGiftEnabled ? 'ok' : 'neutral'} plain data-id="element-6414" data-mp="2mveu14bq45g8">
                    {merchant.campaGiftEnabled ? 'Enabled' : 'Not enabled'}
                  </Badge>
      }, {
        header: 'Commission',
        cell: merchant => <span className="cz-num text-ink-muted" data-id="element-6415" data-mp="35sfz0p5a25k6">
                    {merchant.commissionBps === null ? 'Gift type default' : bpsLabel(merchant.commissionBps)}
                  </span>
      }, {
        header: 'Settlement',
        cell: merchant => <span className="text-ink-muted" data-id="element-6416" data-mp="2rsf0vhgedc8e">
                    {merchant.settlementSchedule}
                    <span className="cz-num mt-0.5 block text-caption text-ink-subtle" data-id="element-6417" data-mp="1kgg0ja09ckpz">
                      {merchant.settlementAccountMasked}
                    </span>
                  </span>
      }, {
        header: 'Risk',
        status: true,
        cell: merchant => <Badge tone={merchant.riskState === 'clear' ? 'ok' : merchant.riskState === 'review' ? 'warn' : 'danger'} data-id="element-6418" data-mp="2ucjq8h3q8cm0">
                    {merchant.riskState}
                  </Badge>
      }, {
        header: 'Redeemed',
        align: 'right',
        cell: merchant => <span className="cz-num font-semibold" data-id="element-6419" data-mp="3i01cjx482ihl">
                    {koboToNaira(redemptions.filter(r => r.merchantId === merchant.id && r.state === 'confirmed').reduce((total, r) => total + r.amountKobo, 0))}
                  </span>
      }]} data-id="element-6411" data-mp="9n5h53139u3j" />
          <p className="border-t border-line px-4 py-3 text-caption text-ink-muted" data-id="element-6420" data-mp="1u31zuzcf7zjy">
            Settlement account numbers are masked here. Full financial detail requires the sensitive-data
            permission, which is separate from gift operations.
          </p>
        </Panel>}

      {tab === 'Risk' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-6421" data-mp="1gac9yjtruahk">
          <Panel title="Risk flags" bodyClassName="p-0" data-id="element-6422" data-mp="1beoj7zzn599y">
            <ul className="divide-y divide-line" data-id="element-6423" data-mp="1q1zx48jap5d8">
              {[{
            id: 'rf-001',
            signal: 'Gift code guessing',
            detail: '14 failed redemption code attempts from one device in 4 minutes. Rate limit engaged; no gift matched.',
            severity: 'high' as const,
            state: 'escalated'
          }, {
            id: 'rf-003',
            signal: 'Circular gifting',
            detail: 'Two accounts gifted the same value back and forth four times in one day.',
            severity: 'medium' as const,
            state: 'open'
          }, {
            id: 'rf-002',
            signal: 'Redemption velocity',
            detail: 'Three redemptions within nine minutes across two merchants.',
            severity: 'low' as const,
            state: 'cleared'
          }].map(flag => <li key={flag.id} className="px-5 py-4" data-id="element-6424" data-mp="18bioiue6pwrn">
                  <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-6425" data-mp="1dc6r8dbs021p">
                    <p className="flex items-center gap-2 text-body font-semibold text-ink" data-id="element-6426" data-mp="2js8xgl9zt2oy">
                      <FlagIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-6427" data-mp="2oqtrg2yniq4y" />
                      {flag.signal}
                    </p>
                    <div className="flex items-center gap-2" data-id="element-6428" data-mp="d371s5i2urdw">
                      <Badge tone={flag.severity === 'high' ? 'danger' : flag.severity === 'medium' ? 'warn' : 'neutral'} data-id="element-6429" data-mp="843xin9x8p6z">
                        {flag.severity} severity
                      </Badge>
                      <Badge tone={flag.state === 'cleared' ? 'ok' : 'warn'} data-id="element-6430" data-mp="3liiacqgdw077">{flag.state}</Badge>
                    </div>
                  </div>
                  <p className="mt-1.5 text-body-sm text-ink-muted" data-id="element-6431" data-mp="3gma27x0lt79j">{flag.detail}</p>
                  {flag.state !== 'cleared' && <div className="mt-3 flex flex-wrap gap-2" data-id="element-6432" data-mp="1etc4bmryzohq">
                      <Button size="sm" variant="secondary" data-id="element-6433" data-mp="1eqpwrn52x8zi">
                        Assign reviewer
                      </Button>
                      <Button size="sm" variant="ghost" data-id="element-6434" data-mp="io52h14755sr">
                        Clear flag
                      </Button>
                    </div>}
                </li>)}
            </ul>
          </Panel>

          <div className="space-y-6" data-id="element-6435" data-mp="9vtk82j8konv">
            <Panel title="Flags are not verdicts" data-id="element-6436" data-mp="dxwyeyxai7cu">
              <p className="flex items-start gap-2 text-body-sm text-ink-muted" data-id="element-6437" data-mp="dwdij9fjpybl">
                <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-6438" data-mp="3w2i2zatk9izj" />
                A risk signal queues a human review. No account is accused, restricted or penalised on
                an automated score alone, and every decision records who made it and why.
              </p>
            </Panel>
            <Panel title="Limits in force" bodyClassName="p-0" data-id="element-6439" data-mp="3r4ynk7l4n060">
              <ul className="divide-y divide-line" data-id="element-6440" data-mp="1lb8gt57j9eig">
                {limits.map(limit => <li key={limit.id} className="px-5 py-3" data-id="element-6441" data-mp="1pdd9a0m8w53i">
                    <p className="text-body-sm font-medium text-ink" data-id="element-6442" data-mp="1par3a5f05xne">{limit.label}</p>
                    <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-6443" data-mp="39ve3m8yflu13">
                      {limit.scope} ·{' '}
                      {limit.valueKobo !== null ? koboToNaira(limit.valueKobo) : ''}
                      {limit.valueKobo !== null && limit.count !== null ? ' / ' : ''}
                      {limit.count !== null ? `${limit.count} max` : ''}
                    </p>
                    <p className="mt-0.5 text-caption text-ink-muted" data-id="element-6444" data-mp="3thk6602qi8we">{limit.note}</p>
                  </li>)}
              </ul>
            </Panel>
          </div>
        </div>}

      {tab === 'Configuration' && <div className="space-y-6" data-id="element-6445" data-mp="1fb26kxwx1l7">
          <Panel title="Gift types" bodyClassName="p-0" data-id="element-6446" data-mp="1gdvt0mmdi4t">
            <ul className="divide-y divide-line" data-id="element-6447" data-mp="285sh22umpb8n">
              {types.map(type => <li key={type.id} className="px-5 py-4" data-id="element-6448" data-mp="287lmxfxf3rfl">
                  <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-6449" data-mp="237z9b5dog969">
                    <p className="text-body font-semibold text-ink" data-id="element-6450" data-mp="1ehuiiat7u4hv">{type.name}</p>
                    <Badge tone={type.enabled ? 'ok' : 'neutral'} plain data-id="element-6451" data-mp="19j9k8gxixuw2">
                      {type.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="mt-1 text-body-sm text-ink-muted" data-id="element-6452" data-mp="zu9g6j4609b0">{type.description}</p>
                  <dl className="mt-2 grid gap-x-6 gap-y-1 text-caption text-ink-muted sm:grid-cols-3" data-id="element-6453" data-mp="uuwuw2sxzaaj">
                    <div data-id="element-6454" data-mp="qtbe46daebv2">
                      Value {koboToNaira(type.minValueKobo)} – {koboToNaira(type.maxValueKobo)}
                    </div>
                    <div data-id="element-6455" data-mp="lv02bq3flra7">Expiry {type.expiryDays ? `${type.expiryDays} days` : 'none'}</div>
                    <div data-id="element-6456" data-mp="gwfc8jhw8nzh">{type.transferability.replace(/_/g, ' ')}</div>
                    <div data-id="element-6457" data-mp="2g8rk5cvwzhgq">Purchase fee {bpsLabel(type.purchaseFeeBps)}</div>
                    <div data-id="element-6458" data-mp="2g5vipekuyanj">Commission {bpsLabel(type.merchantCommissionBps)}</div>
                    <div data-id="element-6459" data-mp="2b726dphb4poy">
                      {type.partialRedemption ? 'Partial allowed' : 'Full redemption only'}
                    </div>
                    <div data-id="element-6460" data-mp="2nr8utmfj12ba">
                      Merchants{' '}
                      {type.eligibleMerchantIds === 'all' ? 'all participating' : `${type.eligibleMerchantIds.length} named`}
                    </div>
                    <div data-id="element-6461" data-mp="2xjn3kz50v96y">
                      Categories{' '}
                      {type.eligibleCategoryIds === 'all' ? 'unrestricted' : type.eligibleCategoryIds.join(', ')}
                    </div>
                    <div data-id="element-6462" data-mp="29qy3yje7vnq4">Funding {type.funding.replace(/_/g, ' ')}</div>
                  </dl>
                </li>)}
            </ul>
            <p className="border-t border-line px-5 py-3 text-caption text-ink-muted" data-id="element-6463" data-mp="2eozprc31215a">
              Every rule an instrument obeys is read from this configuration. Publishing a new
              programme is an administrative action, not a code change, and each change is audited.
            </p>
          </Panel>

          <Panel title="Feature flags" bodyClassName="p-0" data-id="element-6464" data-mp="1l4vlr859k6ri">
            <ul className="divide-y divide-line" data-id="element-6465" data-mp="1l4dkfx3qv2cq">
              {flags.map(flag => <li key={flag.key} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-5 py-3.5" data-id="element-6466" data-mp="1z3d38o4ksn0z">
                  <div className="min-w-0 flex-1" data-id="element-6467" data-mp="1v3ksc4rpm4sg">
                    <p className="text-body font-medium text-ink" data-id="element-6468" data-mp="17d8nj2rrgo63">{flag.label}</p>
                    <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-6469" data-mp="12edwjdjn3taw">
                      {flag.key} · rollout: {flag.rollout}
                    </p>
                    <p className="mt-0.5 text-caption text-ink-muted" data-id="element-6470" data-mp="1g7dbnefiflhx">{flag.note}</p>
                  </div>
                  <Badge tone={flag.enabled ? 'ok' : 'warn'} data-id="element-6471" data-mp="1bdslg8k5rvta">
                    {flag.enabled ? 'On' : 'Off'}
                  </Badge>
                </li>)}
            </ul>
          </Panel>

          <div className="rounded-card border border-warn-100 bg-warn-50 px-5 py-4" data-id="element-6472" data-mp="jltiv3cihuro">
            <p className="text-body font-semibold text-ink" data-id="element-6473" data-mp="9ieiqrqztobx">Compliance gate</p>
            <p className="mt-1.5 text-body-sm text-ink-muted" data-id="element-6474" data-mp="ebr14k52wyw8">
              Before real-money issuance or redemption is enabled, the implementation must be reviewed
              against stored-value, voucher, payments, tax and consumer-protection requirements for
              each market. Calling something a gift does not remove those obligations. Purchase,
              settlement disbursement and bulk issuance stay off until that review is complete and the
              payment providers are onboarded.
            </p>
          </div>
        </div>}

      {tab === 'Audit' && <Panel title="Audit log" description="Immutable records of consequential actions, each attributed to an actor with a reason." bodyClassName="p-0" data-id="element-6475" data-mp="naciwd2q89vy">
          <ul className="divide-y divide-line" data-id="element-6476" data-mp="381v21yqkm3th">
            {audit.map(entry => <li key={entry.id} className="px-5 py-3.5" data-id="element-6477" data-mp="3o99gh853aliy">
                <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-6478" data-mp="3oau9uufo3yf8">
                  <p className="text-body font-medium text-ink" data-id="element-6479" data-mp="3jgq063f0pcag">{entry.action}</p>
                  <p className="cz-num text-caption text-ink-muted" data-id="element-6480" data-mp="3ejpvnwxtghlg">{entry.at}</p>
                </div>
                <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-6481" data-mp="39lel1jzp6326">
                  {entry.entity}
                  {entry.from || entry.to ? ` · ${entry.from ?? '—'} → ${entry.to ?? '—'}` : ''}
                </p>
                <p className="mt-0.5 text-caption text-ink-muted" data-id="element-6482" data-mp="611sdjx0197t">
                  {entry.actor}
                  {entry.reason ? ` · ${entry.reason}` : ''}
                </p>
              </li>)}
          </ul>
        </Panel>}

      <Modal open={reverseTarget !== null} onClose={() => setReverseTarget(null)} title="Reverse a redemption" description="The original record is preserved. A compensating entry returns the value to the instrument." footer={<>
            <Button variant="secondary" onClick={() => setReverseTarget(null)} data-id="element-6484" data-mp="11bow1on7vdkh">
              Cancel
            </Button>
            <Button variant="danger" onClick={() => {
        if (!reverseTarget) return;
        const outcome = reverse(reverseTarget, reverseReason, 'Finance · adaeze.k');
        if (!outcome.ok) {
          setReverseError(outcome.error ?? 'Reversal refused.');
          return;
        }
        setReverseTarget(null);
      }} data-id="element-6485" data-mp="16a9x1qaam19z">
              Reverse redemption
            </Button>
          </>} data-id="element-6483" data-mp="2luiwcvqjfnz5">
        <div className="space-y-4" data-id="element-6486" data-mp="1u0dokza7iakm">
          <Field label="Reason" htmlFor="reverse-reason" hint="Required. Stored on the compensating entry and in the audit log." data-id="element-6487" data-mp="3qt5qjh5jfkoj">
            <Input id="reverse-reason" value={reverseReason} onChange={event => setReverseReason(event.target.value)} placeholder="Duplicate scan confirmed against till receipt" data-id="element-6488" data-mp="3vucybg7okjv7" />
          </Field>
          {reverseError && <p className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2 text-body-sm text-danger-700" data-id="element-6489" data-mp="1lnse0rf1txl">
              <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-6490" data-mp="akj1bkluhi4l" />
              {reverseError}
            </p>}
        </div>
      </Modal>

      <Modal open={freezeTarget !== null} onClose={() => setFreezeTarget(null)} title="Freeze this instrument" description="Freezing stops redemption immediately. It does not remove value or alter history." footer={<>
            <Button variant="secondary" onClick={() => setFreezeTarget(null)} data-id="element-6492" data-mp="u9jop4d2pjtu">
              Cancel
            </Button>
            <Button variant="danger" disabled={!freezeReason.trim()} onClick={() => {
        if (!freezeTarget) return;
        freeze(freezeTarget, freezeReason, 'Risk · tobi.a');
        setFreezeTarget(null);
        setFreezeReason('');
      }} data-id="element-6493" data-mp="p7ucvmi1124p">
              Freeze instrument
            </Button>
          </>} data-id="element-6491" data-mp="6hcxejm7g31a">
        <Field label="Reason" htmlFor="freeze-reason" hint="Required for any override." data-id="element-6494" data-mp="p7reus2mlzjq">
          <Input id="freeze-reason" value={freezeReason} onChange={event => setFreezeReason(event.target.value)} placeholder="Reported stolen by the holder" data-id="element-6495" data-mp="p5wy7pye0tlg" />
        </Field>
      </Modal>
    </div>;
}